import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Tag, Search, Users, Gift, CheckCircle } from 'lucide-react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Switch } from '../ui/switch';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../ui/table';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CouponsManagement = ({ token }) => {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchCoupons(); }, []);

  const fetchCoupons = async () => {
    try {
      const res = await axios.get(`${API}/admin/coupons`, { headers: { Authorization: `Bearer ${token}` } });
      setCoupons(res.data);
    } catch { toast.error('Kuponlar yüklenemedi'); } finally { setLoading(false); }
  };

  const handleToggle = async (code, currentStatus) => {
    try {
      await axios.put(`${API}/admin/coupons/${code}/toggle`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(currentStatus ? 'Kupon devre dışı bırakıldı' : 'Kupon aktif edildi'); 
      fetchCoupons();
    } catch { toast.error('İşlem başarısız'); }
  };

  if (loading) return <div className="text-center py-8">Yükleniyor...</div>;

  const activeCoupons = coupons.filter(c => c.is_active);
  const usedCoupons = coupons.filter(c => c.used_count > 0);

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl p-6 shadow-sm">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-basil/10 rounded-full flex items-center justify-center">
              <Gift className="w-5 h-5 text-basil" />
            </div>
            <div>
              <p className="text-sm text-charcoal/60">Toplam Kupon</p>
              <p className="font-playfair text-2xl font-bold text-charcoal">{coupons.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-6 shadow-sm">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-charcoal/60">Aktif Kuponlar</p>
              <p className="font-playfair text-2xl font-bold text-green-600">{activeCoupons.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-6 shadow-sm">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-terracotta/10 rounded-full flex items-center justify-center">
              <Users className="w-5 h-5 text-terracotta" />
            </div>
            <div>
              <p className="text-sm text-charcoal/60">Kullanılan Kupon</p>
              <p className="font-playfair text-2xl font-bold text-terracotta">{usedCoupons.length}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Coupons Table */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="p-4 border-b border-stone-100">
          <h2 className="font-playfair text-xl font-semibold">İndirim Kuponları</h2>
          <p className="text-sm text-charcoal/60">%10 indirim kuponları (CENTRAL001 - CENTRAL100)</p>
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Kupon Kodu</TableHead>
                <TableHead>İndirim</TableHead>
                <TableHead>Kullanım</TableHead>
                <TableHead>Durum</TableHead>
                <TableHead className="text-right">İşlem</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {coupons.slice(0, 20).map((coupon) => (
                <TableRow key={coupon.code}>
                  <TableCell className="font-mono font-semibold">{coupon.code}</TableCell>
                  <TableCell>%{coupon.discount_percent}</TableCell>
                  <TableCell>{coupon.used_count} kez</TableCell>
                  <TableCell>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${coupon.is_active ? 'bg-green-100 text-green-700' : 'bg-stone-100 text-stone-600'}`}>
                      {coupon.is_active ? 'Aktif' : 'Pasif'}
                    </span>
                  </TableCell>
                  <TableCell className="text-right">
                    <Switch checked={coupon.is_active} onCheckedChange={() => handleToggle(coupon.code, coupon.is_active)} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        {coupons.length > 20 && (
          <div className="p-4 text-center text-charcoal/60 text-sm border-t">
            İlk 20 kupon gösteriliyor. Toplam {coupons.length} kupon mevcut.
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default CouponsManagement;
